// Phase 34 · 20b — Backtrack.
//
// The screen's argument is that a decision is not a job: the machine colouring
// on the actor column is the only thing separating the two, and it is real
// here because audit_logs carries a server-derived actor_type. FAILURES is a
// tab rather than a filter chip because it is the reason anyone opens this
// screen unprompted.
//
// Everything the previous (unmounted) version could do still works: search,
// the filter panel, refresh, CSV export, load-more paging and the row detail
// modal. The stats cards are gone because the rail says the same three things
// with an argument attached.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, RefreshCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { useAuditLogs } from '../../hooks/useAudit';
import { AuditLog, AuditSummary, auditService } from '../../services/audit.service';
import { AuditLogFilters } from './components/AuditLogFilters';
import { AuditLogDetail } from './components/AuditLogDetail';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import {
  actorLabel,
  changeDetail,
  eventKind,
  eventLabel,
  eventsByHour,
  failureCause,
  isFailure,
  isMachineActor,
  recordRef,
  summaryActors,
  summaryFailureCount,
  summaryKindCounts,
  tallyActors,
  timeLabel,
} from './backtrack';

const TABS = [
  ['all', 'All'],
  ['tickets', 'Tickets'],
  ['automations', 'Automations'],
  ['knowledge', 'Knowledge'],
  ['failures', 'Failures'],
] as const;

type Tab = (typeof TABS)[number][0];

const CARD: React.CSSProperties = {
  borderRadius: 10,
  boxShadow: 'var(--co-shadow-card)',
};
const HEADER_CARD: React.CSSProperties = { borderRadius: 14, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const HEADER_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');

function matchesTab(log: AuditLog, tab: Tab): boolean {
  if (tab === 'all') return true;
  if (tab === 'failures') return isFailure(log);
  const kind = eventKind(log);
  if (tab === 'tickets') return kind === 'ticket';
  if (tab === 'automations') return kind === 'automation';
  return kind === 'knowledge';
}

/**
 * `embedded` is the Organization Settings mount: the settings shell already
 * pads the canvas, scrolls the column and carries the status + bell, so the
 * page drops its own gutter, fixed height and utility cluster.
 */
export const AuditLogsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState<Tab>('all');

  const { logs, total, loading, error, filter, setFilter, refresh, loadMore, hasMore } =
    useAuditLogs({ limit: 50 });

  // Counts for the WHOLE filtered trail, from the server's own aggregate over
  // the same WHERE the list pages through. Without it every number on this
  // screen would describe the 50 rows in hand while claiming to describe the
  // trail — which is the specific lie this page cannot afford.
  const [summary, setSummary] = useState<AuditSummary | null>(null);

  const loadSummary = useCallback(async (f: typeof filter) => {
    try {
      const next = await auditService.getAuditSummary({
        startDate: f.startDate,
        endDate: f.endDate,
        action: f.action,
        resource: f.resource,
        resourceId: f.resourceId,
        userId: f.userId,
        userEmail: f.userEmail,
        status: f.status,
        search: f.search,
      });
      setSummary(next);
    } catch {
      // Degrade to page-scoped counts, labelled as such, rather than blocking
      // the trail on an aggregate.
      setSummary(null);
    }
  }, []);

  useEffect(() => {
    void loadSummary(filter);
  }, [
    loadSummary,
    filter.startDate,
    filter.endDate,
    filter.action,
    filter.resource,
    filter.resourceId,
    filter.userId,
    filter.userEmail,
    filter.status,
    filter.search,
  ]);

  const rows = useMemo(() => logs.filter((l) => matchesTab(l, tab)), [logs, tab]);
  const failures = useMemo(() => logs.filter(isFailure), [logs]);
  const hours = useMemo(() => eventsByHour(logs), [logs]);
  const peak = Math.max(1, ...hours.map((h) => h.count));

  const kindCounts = useMemo(
    () => (summary ? summaryKindCounts(summary.byResource) : null),
    [summary]
  );
  const failureTotal = summary ? summaryFailureCount(summary.byStatus) : null;
  /** True while the trail holds fewer failures than the whole set contains. */
  const failuresPartial = failureTotal !== null && failureTotal > failures.length;

  // Machines complete, people top-ten — both across the whole filtered trail.
  // Falls back to the loaded page when the aggregate is unavailable.
  const actors = useMemo(
    () => (summary ? summaryActors(summary) : tallyActors(logs)),
    [summary, logs]
  );
  const actorsScoped = !summary;

  const cause = useMemo(
    () => failureCause(failures, (a) => auditService.getActionLabel(a), failuresPartial),
    [failures, failuresPartial]
  );
  const earliestFailureHour = failures.length
    ? Math.min(...failures.map((f) => new Date(f.createdAt).getHours()))
    : null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilter({ ...filter, search: searchQuery, page: 1 });
  };

  const handleExport = async () => {
    try {
      const { exportId } = await auditService.createExport({
        format: 'csv',
        startDate: filter.startDate,
        endDate: filter.endDate,
        action: filter.action,
        resource: filter.resource,
      });
      alert(`Export started. Export ID: ${exportId}`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  /**
   * A tab's number is about the trail, not the page — so it comes from the
   * aggregate whenever there is one. `null` when there is not: no number at
   * all beats a page-sized one wearing a trail-sized label.
   */
  const tabCount = (t: Tab): number | null => {
    if (!summary || !kindCounts) return null;
    if (t === 'all') return summary.total;
    if (t === 'failures') return failureTotal;
    if (t === 'tickets') return kindCounts.ticket;
    if (t === 'automations') return kindCounts.automation;
    return kindCounts.knowledge;
  };

  const tabTotal = tabCount(tab);

  return (
    <div
      className={
        embedded
          ? 'flex flex-col gap-[14px]'
          : 'flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4'
      }
    >
      {/* ---------- header card ---------- */}
      <header className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')} style={HEADER_CARD}>
        {/* Row 1: title block + the utility cluster, nothing else. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              Audit log
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              {/* No count when the load failed: "0 of 0 events" would state that
                  the trail is empty, which nobody knows. */}
              {error
                ? 'Every state change, in order · events could not be loaded'
                : <>
                    Every state change, in order · {logs.length} of {total} events
                    {failureTotal ? ` · ${failureTotal} failed` : ''}
                  </>}
            </p>
          </div>
          {!embedded && (
            <div className="flex h-[34px] flex-none items-center">
              <PageHeaderMeta />
            </div>
          )}
        </div>

        {/* Row 2: every other header control, wrapping when space runs out. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          <form onSubmit={handleSearch} className="relative w-[280px] max-w-full flex-none">
            <Search className="pointer-events-none absolute left-[11px] top-1/2 z-10 h-[14px] w-[14px] -translate-y-1/2 text-[var(--co-ink-3)]" />
            <input
              type="text"
              placeholder="Search by user, resource or action…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-[34px] w-full rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-[32px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setFilter({ ...filter, search: '', page: 1 });
                }}
                aria-label="Clear search"
                className="absolute right-[10px] top-1/2 z-10 -translate-y-1/2 text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
              >
                <X className="h-[14px] w-[14px]" />
              </button>
            )}
          </form>

          <div className="flex-1" />

          <div className="flex flex-none flex-wrap items-center gap-[10px]">
            <button
              type="button"
              onClick={() => {
                void refresh();
                void loadSummary(filter);
              }}
              disabled={loading}
              className={HEADER_CONTROL}
            >
              <RefreshCw className={clsx('h-[14px] w-[14px]', loading && 'animate-spin')} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={clsx(
                HEADER_CONTROL,
                showFilters && 'border-[var(--co-brass)] text-[var(--co-brass-ink)]'
              )}
            >
              <SlidersHorizontal className="h-[14px] w-[14px]" />
              Filters
            </button>
            <button
              type="button"
              onClick={handleExport}
              // Nothing loaded means nothing to export; the request would fail too.
              disabled={Boolean(error)}
              title={error ? 'Available once the audit log loads' : undefined}
              className={HEADER_CONTROL}
            >
              <Download className="h-[14px] w-[14px]" />
              Export
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
            {TABS.map(([key, label]) => {
              const on = key === tab;
              const n = tabCount(key);
              const hasCount = n !== null && n !== undefined;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  aria-pressed={on}
                  className="co-mono flex items-center gap-[6px] rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
                  style={
                    on
                      ? {
                          background: 'var(--co-raised)',
                          color: 'var(--co-ink)',
                          fontWeight: 600,
                          boxShadow: 'var(--co-shadow)',
                        }
                      : { color: 'var(--co-ink-3)', fontWeight: 500 }
                  }
                >
                  {label}
                  {hasCount && (
                    <span
                      className="font-semibold"
                      style={{
                        color: on
                          ? 'var(--co-brass-ink)'
                          : key === 'failures' && (n as number) > 0
                            ? 'var(--co-burgundy)'
                            : 'var(--co-ink-3)',
                      }}
                    >
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

        </div>

        {showFilters && (
          <div className="mt-4 border-t border-[var(--co-line-subtle)] pt-4">
            <AuditLogFilters filter={filter} onFilterChange={setFilter} />
          </div>
        )}
      </header>

      {/* Below 1280 the rail moves under the table rather than squeezing it —
          DETAIL and ACTOR never drop, they are the screen. */}
      <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto xl:flex-row xl:overflow-hidden">
        <section
          className={clsx(
            CARD_CLASS,
            'flex min-h-[320px] min-w-0 flex-1 flex-col overflow-hidden'
          )}
          style={CARD}
        >
          {/* #20577 — header and rows share one horizontal-scroll box with a
              min-width, so when the card is squeezed (narrow window, visible
              rail, zoom) the whole table scrolls sideways together instead of
              the fixed columns overlapping the headers ("DETACTOR") and the
              Record column being clipped. At comfortable widths it is a no-op. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-x-auto co-scrollbar-none">
          <div className="flex min-h-0 min-w-[760px] flex-1 flex-col">
          <div className="co-mono flex flex-none items-center border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px] text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)]">
            <div className="w-[86px] flex-none">Time</div>
            <div className="w-[180px] flex-none">Event</div>
            <div className="min-w-0 flex-1">Detail</div>
            <div className="w-[168px] flex-none">Actor</div>
            <div className="w-24 flex-none pr-5 text-right">Record</div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto co-scrollbar-none">
            {error && (
              <p
                className="border-b px-5 py-3 text-[12.5px]"
                style={{
                  borderColor: 'var(--co-burgundy-line)',
                  background: 'var(--co-burgundy-wash)',
                  color: 'var(--co-burgundy)',
                }}
              >
                {/* Never the transport's own words ("Request failed with status
                    code 500"): they tell the reader nothing they can act on. */}
                The audit log could not be loaded. Try Refresh; if it keeps failing, contact
                your administrator.
              </p>
            )}
            {loading && rows.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-6 w-6 animate-spin text-[var(--co-ink-3)]" />
              </div>
            ) : error ? null : rows.length === 0 ? (
              <p className="px-5 py-12 text-[13px] text-[var(--co-ink-3)]">
                No events in this view.
              </p>
            ) : (
              rows.map((log) => (
                <EventRow
                  key={log.id}
                  log={log}
                  onOpen={() => setSelectedLog(log)}
                  onNavigate={navigate}
                />
              ))
            )}
          </div>
          </div>
          </div>

          <div
            className="flex flex-none flex-wrap items-center gap-[14px] border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-3"
            style={{ borderRadius: '0 0 9px 9px' }}
          >
            <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              {/* The rows on screen, and how many the tab actually holds —
                  a trail that pages must never imply it has shown everything. */}
              {error
                ? 'Events could not be loaded'
                : tabTotal !== null && tabTotal > rows.length
                  ? `${rows.length} of ${tabTotal} events · newest first`
                  : `${rows.length} events · newest first`}
            </span>
            <div className="flex-1" />
            <span className="text-[12px] text-[var(--co-ink-3)]">
              A machine-coloured actor means nobody chose this — the difference between a
              decision and a job.
            </span>
            {hasMore && (
              <button type="button" onClick={loadMore} disabled={loading} className={CONTROL}>
                Load more
              </button>
            )}
          </div>
        </section>

        <aside className="flex w-full flex-none flex-col gap-[14px] empty:hidden xl:w-[332px] xl:overflow-y-auto">
          {/* absent at zero — never "0 failures today". Present whenever the
              trail holds a failure, even if none of them are on this page. */}
          {(cause || (failureTotal ?? 0) > 0) && (
            <div
              className="flex-none border px-[18px] py-4"
              style={{
                ...CARD,
                borderColor: 'var(--co-burgundy-line)',
                background: 'var(--co-burgundy-wash)',
              }}
            >
              <span
                className="co-mono text-[10px] font-semibold uppercase tracking-[.16em]"
                style={{ color: 'var(--co-burgundy)' }}
              >
                {failureTotal ?? failures.length} failure
                {(failureTotal ?? failures.length) === 1 ? '' : 's'}
              </span>
              {/* the CAUSE, not the code */}
              <p className="mt-[9px] text-[12.5px] leading-[1.6] text-[var(--co-ink-2)]">
                {cause
                  ? cause.headline
                  : 'None of them are on this page — load more, or open the FAILURES tab.'}
              </p>
              {cause?.message && (
                <p className="co-mono mt-2 break-words text-[10.5px] leading-[1.5] text-[var(--co-ink-3)]">
                  {cause.message}
                </p>
              )}
              <div
                className="mt-3 border-t pt-3"
                style={{ borderColor: 'var(--co-burgundy-line)' }}
              >
                <button
                  type="button"
                  onClick={() => setTab('failures')}
                  className="co-mono text-[10px] font-semibold uppercase tracking-[.11em]"
                  style={{ color: 'var(--co-burgundy)' }}
                >
                  See every failure →
                </button>
              </div>
            </div>
          )}

          {hours.length > 0 && (
            <div className={clsx(CARD_CLASS, 'flex-none px-[18px] py-4')} style={CARD}>
              <span className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Events by hour
              </span>
              {/* Deliberately the loaded events: an hour cut server-side would
                  be cut in the database's timezone, not the reader's. */}
              <span className="co-mono ml-2 text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                · {logs.length} loaded
              </span>
              <div className="mt-[14px] flex h-20 items-end gap-[7px]">
                {hours.map((h) => (
                  <div key={h.hour} className="flex h-full flex-1 flex-col justify-end gap-[6px]">
                    <div
                      className="rounded-t-[3px]"
                      style={{
                        height: `${h.count ? (h.count / peak) * 100 : 2}%`,
                        background: h.count
                          ? h.failed
                            ? 'var(--co-burgundy)'
                            : 'var(--co-line-strong)'
                          : 'var(--co-line-subtle)',
                      }}
                    />
                    <div className="co-mono text-center text-[8.5px] text-[var(--co-ink-3)]">
                      {h.hour}
                    </div>
                  </div>
                ))}
              </div>
              {earliestFailureHour !== null && (
                <p className="mt-[11px] border-t border-[var(--co-line-subtle)] pt-[11px] text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
                  {earliestFailureHour < 9
                    ? 'The first failure landed before 09:00, before anyone was watching.'
                    : `The first failure landed at ${String(earliestFailureHour).padStart(2, '0')}:00.`}
                </p>
              )}
            </div>
          )}

          {actors.length > 0 && (
            <div
              className={clsx(CARD_CLASS, 'flex min-h-0 flex-none flex-col px-[18px] py-4')}
              style={CARD}
            >
              <span className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Who changed things
              </span>
              <span className="co-mono ml-2 text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                · {actorsScoped ? `${logs.length} loaded` : 'whole trail'}
              </span>
              <div className="mt-[13px] flex flex-col gap-[11px]">
                {actors.slice(0, 8).map((a) => (
                  <div key={a.key} className="flex items-center gap-[10px]">
                    {a.machine ? (
                      <SysChip />
                    ) : (
                      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-[7px] bg-[var(--co-ink-3)] text-[10px] font-semibold text-[var(--co-ink-invert)]">
                        {a.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--co-ink-2)]">
                      {a.name}
                    </span>
                    <span className="co-mono text-[12px] font-semibold text-[var(--co-ink)]">
                      {a.count}
                    </span>
                  </div>
                ))}
              </div>
              {actors[0]?.machine && (
                <p className="mt-3 border-t border-[var(--co-line-subtle)] pt-3 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
                  The system made more changes than any person. That is normal — and worth
                  knowing before you attribute an outcome.
                </p>
              )}
            </div>
          )}
        </aside>
      </div>

      {selectedLog && (
        <AuditLogDetail log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
};

/** 24px square, machine-coloured. Requires the server-derived actor_type. */
function SysChip() {
  return (
    <span
      className="co-mono flex h-6 w-6 flex-none items-center justify-center rounded-md border text-[9px] font-semibold"
      style={{
        borderColor: 'var(--co-machine-line)',
        background: 'var(--co-machine-wash)',
        color: 'var(--co-machine-ink)',
      }}
    >
      SYS
    </span>
  );
}

function EventRow({
  log,
  onOpen,
  onNavigate,
}: {
  log: AuditLog;
  onOpen: () => void;
  onNavigate: (to: string) => void;
}) {
  const failed = isFailure(log);
  const machine = isMachineActor(log);
  const detail = changeDetail(log);
  const ref = recordRef(log);
  const priority = log.action === 'PERMISSION_CHANGE' || log.action === 'SETTINGS_CHANGE';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="flex cursor-pointer items-start border-b border-[var(--co-line-subtle)] px-5 py-3 text-left hover:bg-[var(--co-selected)]"
      style={{
        borderLeft: `2px solid ${
          failed
            ? 'var(--co-burgundy)'
            : priority
              ? 'var(--co-terracotta)'
              : 'transparent'
        }`,
      }}
    >
      <div className="co-mono w-[86px] flex-none text-[11.5px] text-[var(--co-ink-3)]">
        {timeLabel(log)}
      </div>
      <div
        className="w-[180px] flex-none pr-3 text-[13px] font-semibold"
        style={{ color: failed ? 'var(--co-burgundy)' : 'var(--co-ink)' }}
      >
        {eventLabel(log, auditService.getActionLabel(log.action))}
      </div>
      {/* before → after only. No free-text reason suffix. */}
      <div className="min-w-0 flex-1 pr-4 text-[12.5px] leading-[1.5] text-[var(--co-ink-2)]">
        {detail ?? (
          <span className="text-[var(--co-ink-3)]">
            {log.resourceName || 'No field change recorded'}
          </span>
        )}
      </div>
      <div className="flex w-[168px] flex-none items-center gap-2">
        {machine ? (
          <>
            <SysChip />
            <span
              className="co-mono truncate text-[10.5px] uppercase tracking-[.08em]"
              style={{ color: 'var(--co-machine-ink)' }}
            >
              {actorLabel(log)}
            </span>
          </>
        ) : (
          <>
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-[7px] bg-[var(--co-ink-3)] text-[10px] font-semibold text-[var(--co-ink-invert)]">
              {actorLabel(log).charAt(0).toUpperCase()}
            </span>
            <span className="truncate text-[12.5px] text-[var(--co-ink-2)]">
              {actorLabel(log)}
            </span>
          </>
        )}
      </div>
      <div className="w-24 flex-none pr-5 text-right">
        {ref &&
          (ref.href ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(ref.href as string);
              }}
              className="co-mono text-[11px] font-semibold text-[var(--co-brass-ink)] hover:underline"
            >
              {ref.label}
            </button>
          ) : (
            <span className="co-mono text-[11px] font-semibold text-[var(--co-ink-3)]">
              {ref.label}
            </span>
          ))}
      </div>
    </div>
  );
}

export default AuditLogsPage;
